import React, {
  ReactElement,
  LazyExoticComponent,
  Suspense,
  useEffect,
} from 'react';
import Routes from 'Routes';
import styled from 'styled-components';
import {
  fetchMoreMetaBooths,
  selectBoothPageInfo,
  selectTotalFilteredBoothsCount,
} from 'models/cms/booths';
import XtraBreadcrumbs from 'components/XtraBreadcrumbs';
import { useTranslation } from 'react-i18next';
import { selectLocale } from 'models/localization';
import HeaderComponent from 'pages/hall/LazyBoothList/HeaderComponent';
import { useDispatch, useSelector } from 'react-redux';

import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import FloatingFooter from 'components/footer_v2/FloatingFooter';
import HiddenFooterContainer from 'components/footer_v2/HiddenFooterContainer';
import GenericBoothPreview from './LazyBoothList/GenericBoothPreview';
import BreadcrumbsNoFilterContainer from './BreadcrumbsNoFilterContainer';

const IEBreadcrumbsNoFilterContainer = styled(BreadcrumbsNoFilterContainer)<{ hasFilter: boolean }>`
  flex-basis: 100%;
  display: block;
  padding-top: 16px;

  @media (min-width: 960px) {
    display: ${({ hasFilter }) => (hasFilter ? 'none' : 'block')};
  }
`;

const IEBoothListContainer = styled.div`
  min-width: 100%;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  flex: 1 0 auto;
`;

const StyledHiddenFooterContainer = styled(HiddenFooterContainer)`
  flex-basis: 100%;
`;

const HallPageHeader: LazyExoticComponent<() => ReactElement> | (() => ReactElement) = () => <div />;

const MainContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-x: hidden;
  overflow-y: auto;
  height: 100%;
`;

const StyledHeaderComponent = styled(HeaderComponent)`
  position: relative;
`;

export const CMSFetchMoreIndicator = () => {
  const dispatch = useDispatch();
  const pageInfo = useSelector(selectBoothPageInfo);

  useEffect(() => {
    if (pageInfo.fetching) return;
    dispatch(fetchMoreMetaBooths());
  }, [dispatch, pageInfo]);

  if (!pageInfo.fetching) return null;

  return <CircularLoadingIndicator />;
};

const IEBoothList = ({
  exhibitorIds = [],
  hasFilter,
  isFiltered,
}: {
  exhibitorIds: string[];
  hasFilter: boolean;
  isFiltered: boolean;
}) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const { pageNames: { lobby, hall } } = useSelector((s: any) => s.eventConfigs);
  const totalFilteredBoothsCount = useSelector(selectTotalFilteredBoothsCount);
  const showFilterResult = isFiltered && totalFilteredBoothsCount > 0;
  const lobbyName = lobby?.[locale] || t('page.lobby', 'Lobby');
  const hallName = hall?.[locale] || t('page.hall', 'Exhibition Hall');

  return (
    <MainContainer>
      {showFilterResult && (
        <StyledHeaderComponent
          isFiltered={isFiltered}
          filterLength={totalFilteredBoothsCount}
          headerOffset={0}
          hasFilter={hasFilter}
        />
      )}
      <IEBoothListContainer>
        <IEBreadcrumbsNoFilterContainer hasFilter={hasFilter}>
          <XtraBreadcrumbs
            paths={
              [
                { name: lobbyName, to: Routes.lobby },
                { name: hallName },
              ]
            }
          />
        </IEBreadcrumbsNoFilterContainer>
        <Suspense fallback={null}>
          {
            !showFilterResult && <HallPageHeader />
          }
        </Suspense>
        {exhibitorIds.map((boothId, exhibitorIndex) => (
          <GenericBoothPreview
            key={boothId}
            index={exhibitorIndex}
            boothId={boothId}
          />
        ))}
        <StyledHiddenFooterContainer>
          <FloatingFooter hashState={Routes.hall} />
        </StyledHiddenFooterContainer>
      </IEBoothListContainer>
      <CMSFetchMoreIndicator />
    </MainContainer>
  );
};

export default IEBoothList;
